/**
 * Authentication API - handles authentication endpoints that don't require auth tokens
 */
import { ApiResponse, ApisauceInstance, create } from "apisauce"
import { mapCognitoRoleCode } from "types/role"

import { delay } from "src/utils/delay"
import type {
  ApiConfig,
  AuthTokens,
  ChannelBrandData,
  ChannelMediaItem,
  IntroMessageResponse,
  LoginMeta,
  LoginResponseUnion,
  RefreshResponse,
  SponsorInfo,
  UnauthPitchAudio,
} from "./api.types"
import { isMultiSponsorResponse } from "./api.types"
import { GeneralApiProblem, getGeneralApiProblem } from "./apiProblem"

/**
 * Manages authentication requests (login, refresh, logout, password management)
 * These endpoints don't require auth tokens (except logout which takes token directly)
 */
export class AuthApi {
  apiSauce: ApisauceInstance
  config: ApiConfig

  /**
   * Set up our Auth API instance
   */
  constructor({ config }: { config: ApiConfig }) {
    this.config = config
    if (!this.config.url) {
      throw new Error("Auth URL is not configured")
    }

    this.apiSauce = create({
      baseURL: this.config.url,
      timeout: this.config.timeout,
      headers: {
        Accept: "application/json",
      },
    })
  }

  async login({
    username,
    password,
  }: {
    username: string
    password: string
  }): Promise<{ kind: "ok"; result: AuthTokens; loginMeta?: LoginMeta } | GeneralApiProblem> {
    console.log("[AUTH_API] login", username)
    const response: ApiResponse<LoginResponseUnion> = await this.apiSauce.post(
      `login`,
      { username, password },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )
    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    const data = response.data?.AuthenticationResult
    if (!data || !data.ExpiresIn) {
      return { kind: "bad-data", data: response }
    }
    const result = {
      ...data,
      // Convert expiresIn to an absolute timestamp in the user's local time
      ExpiresAt: Date.now() + data.ExpiresIn * 1000,
    }

    // FIXME: the default sponsor being the first in the list isn't documented, we should probably have an new design
    // that indicates current sponsor instead of needing this default sponsor + reset sponsor protocol
    const defaultSponsorIdx = 0

    // Check if this is a multi-sponsor response
    // FIXME: the backend should really only return multisponsor type, as the multisponsor can just
    // have a single sponsor in the list. It also doesn't specify which is currently selected.
    if (response.data && isMultiSponsorResponse(response.data)) {
      const availableSponsors = response.data.available_sponsors
      const sponsorData = availableSponsors[defaultSponsorIdx]
      const sponsors: SponsorInfo[] = availableSponsors.map((s) => ({
        sponsor_id: s.sponsor_id,
        sponsor_name: s.sponsor_name ?? s.environment_name,
        environment_name: s.environment_name,
        environment_url: s.environment_url ?? s.target_urls?.web_server_url ?? "",
        default_role: mapCognitoRoleCode(s.role) ?? s.default_role,
        available_roles: s.available_roles,
        cognito_role: s.cognito_role,
      }))
      // Only require channel selection when there is genuinely more than one
      // selectable channel. The backend returns the multi-sponsor shape (with an
      // `available_sponsors` array) even when an account has a single active
      // channel — e.g. when its other channels are disabled/removed and excluded
      // from the list. In that case the picker would show a single option, so we
      // treat it like a single-sponsor login and bootstrap straight into it.
      const loginMeta: LoginMeta = {
        sponsorId: sponsorData?.sponsor_id,
        environmentName: sponsorData?.environment_name,
        targetUrls: {
          smeServerUrl: sponsorData?.target_urls?.sme_server_url,
          webServerUrl: sponsorData?.target_urls?.web_server_url,
        },
        requiresSponsorSelection: availableSponsors.length > 1,
        sponsors,
      }

      return { kind: "ok", result, loginMeta }
    } else {
      // Handle single sponsor response
      const data = response.data
      const sponsors: SponsorInfo[] = data?.sponsor_id
        ? [
            {
              sponsor_id: data.sponsor_id,
              sponsor_name: data.sponsor_name ?? data.environment_name ?? data.sponsor_id,
              environment_name: data.environment_name ?? "",
              environment_url: data.environment_url ?? data.target_urls?.web_server_url ?? "",
              default_role: mapCognitoRoleCode(data.role) ?? data.default_role,
              available_roles: data.available_roles,
              cognito_role: data.cognito_role,
            },
          ]
        : []
      const loginMeta: LoginMeta = {
        sponsorId: data?.sponsor_id,
        environmentName: data?.environment_name,
        targetUrls: {
          smeServerUrl: data?.target_urls?.sme_server_url,
          webServerUrl: data?.target_urls?.web_server_url,
        },
        // Sponsor selection isn't possible with single, so not required
        requiresSponsorSelection: false,
        sponsors,
      }

      return { kind: "ok", result, loginMeta }
    }
  }

  async changePassword({
    email,
    oldPassword,
    newPassword,
  }: {
    email: string
    oldPassword: string
    newPassword: string
  }): Promise<{ kind: "ok"; message: string } | GeneralApiProblem> {
    console.log("[AUTH_API] Change Password (/forgot)")
    // This endpoint is a bit of a misnomer, it's actually to change the user's password and they must be able to provide their existing password.
    const response: ApiResponse<{ message: string }> = await this.apiSauce.post(`forgot`, {
      username: email,
      old_password: oldPassword,
      new_password: newPassword,
    })
    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) {
        return problem
      }
    }

    const message = response.data?.message
    if (!message) {
      return { kind: "bad-data", data: response }
    }
    return { kind: "ok", message }
  }

  async forgotPassword({
    email,
  }: {
    email: string
  }): Promise<{ kind: "ok"; message: string } | GeneralApiProblem> {
    console.log("[AUTH_API] Forgot Password")
    const response: ApiResponse<{ detail: string }> = await this.apiSauce.post("forgot-password", {
      email,
    })

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) {
        return problem
      }
    }
    console.log("[AUTH_API] Forgot Password Response", response.data)
    const message = response.data?.detail // "Password reset email sent successfully"
    if (!message) {
      return { kind: "bad-data", data: response }
    }
    return { kind: "ok", message }
  }

  async resetForgottenPassword({
    email,
    code,
    newPassword,
  }: {
    email: string
    code: string
    newPassword: string
  }): Promise<{ kind: "ok"; message: string } | GeneralApiProblem> {
    console.log("[AUTH_API] Reset Forgotten Password (confirm-forgot-password)")
    const response: ApiResponse<{ detail: string }> = await this.apiSauce.post(
      "confirm-forgot-password",
      {
        email,
        confirmation_code: code,
        new_password: newPassword,
      },
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) {
        return problem
      }
    }

    const message = response.data?.detail // "Password reset successfully"
    if (!message) {
      return { kind: "bad-data", data: response }
    }
    return { kind: "ok", message }
  }

  async getChannelInfo(
    channelId: string,
  ): Promise<{ kind: "ok"; data: ChannelBrandData } | GeneralApiProblem> {
    const response: ApiResponse<ChannelBrandData> = await this.apiSauce.get(
      `channel_info/${encodeURIComponent(channelId)}`,
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", data: response.data }
  }

  async getIntroMessage(
    channelId: string,
  ): Promise<{ kind: "ok"; data: IntroMessageResponse } | GeneralApiProblem> {
    const response: ApiResponse<IntroMessageResponse> = await this.apiSauce.get(
      `intro_message/${encodeURIComponent(channelId)}`,
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", data: response.data }
  }

  /**
   * Fetches the subscription-benefits spoken message for a specific content module.
   * Endpoint: GET /cm_subscription_benefits/<channelId>/<taskId>
   * Returns the same shape as IntroMessageResponse (audio_url, speech_marks, avatar_id, psk_name).
   */
  async getCmSubscriptionBenefits(
    channelId: string,
    taskId: string,
  ): Promise<{ kind: "ok"; data: IntroMessageResponse } | GeneralApiProblem> {
    const response: ApiResponse<IntroMessageResponse> = await this.apiSauce.get(
      `cm_subscription_benefits/${encodeURIComponent(channelId)}/${encodeURIComponent(taskId)}`,
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", data: response.data }
  }

  async getChannelMedia(
    channelId: string,
  ): Promise<{ kind: "ok"; data: ChannelMediaItem[] } | GeneralApiProblem> {
    const response: ApiResponse<ChannelMediaItem[]> = await this.apiSauce.get(
      `channel_media/${encodeURIComponent(channelId)}`,
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", data: response.data }
  }

  /**
   * Fetch pregenerated PSK welcome audio for a media-first channel.
   * Endpoint is TBD on the backend; returns not-found until live.
   */
  async getChannelWelcomeAudio(
    channelId: string,
  ): Promise<{ kind: "ok"; data: UnauthPitchAudio } | GeneralApiProblem> {
    const response: ApiResponse<UnauthPitchAudio> = await this.apiSauce.get(
      `channel_welcome_audio/${encodeURIComponent(channelId)}`,
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", data: response.data }
  }

  /**
   * Fetch pregenerated CM pitch audio for a specific content module.
   * Endpoint is TBD on the backend; returns not-found until live.
   */
  async getCmPitchAudio(
    channelId: string,
    taskId: string,
  ): Promise<{ kind: "ok"; data: UnauthPitchAudio } | GeneralApiProblem> {
    const response: ApiResponse<UnauthPitchAudio> = await this.apiSauce.get(
      `channel_media/${encodeURIComponent(channelId)}/${encodeURIComponent(taskId)}/pitch_audio`,
    )

    if (!response.ok) {
      const problem = getGeneralApiProblem(response)
      if (problem) return problem
    }

    if (!response.data) {
      return { kind: "bad-data", data: response }
    }

    return { kind: "ok", data: response.data }
  }

  async refresh(
    refreshToken: string,
  ): Promise<{ kind: "ok"; result: AuthTokens } | GeneralApiProblem> {
    const MAX_ATTEMPTS = 5
    const BASE_DELAY_MS = 1000

    console.log("[AUTH_API] refresh: refreshing token...")

    let lastProblem: GeneralApiProblem | undefined

    // Retry logic to handle temporary network issues
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      console.debug(`[AUTH_API] refresh: Attempt ${attempt} of ${MAX_ATTEMPTS}`)

      // Attempt to refresh the token using ApiSauce
      const response: ApiResponse<RefreshResponse> = await this.apiSauce.post(`refresh`, {
        refresh_token: refreshToken,
      })

      // Handle successful response.
      if (response.ok) {
        console.debug("[AUTH_API] refresh: Response succeeded, parsing response...")

        const data = response.data?.AuthenticationResult
        if (!data || !data.ExpiresIn) {
          console.warn("[AUTH_API] refresh: Response is not valid, returning bad-data")
          return { kind: "bad-data", data: response.data }
        }

        // If we have a successful response, return the result
        console.log("[AUTH_API] refresh: Successfully refreshed token")
        const result = {
          ...data,
          // RefreshToken is not returned in the response.
          // We aren't using refresh token rotation, so any login extends the
          // expiration time of the current RefreshToken instead of getting a new one.
          // What's actually "refreshed" is the AccessToken.
          RefreshToken: refreshToken,
          ExpiresAt: Date.now() + data.ExpiresIn * 1000,
        }
        return { kind: "ok", result }
      }

      // Handle response errors. Some are retriable and some are not.
      if (!response.ok) {
        // For other errors, get the problem and check if it should retry
        const problem = getGeneralApiProblem(response)

        if (problem.kind === "unauthorized" || problem.kind === "forbidden") {
          // If we get a 401 or 403, the refresh token is invalid - don't retry because we need a new session
          console.error("[AUTH_API] refresh: Refresh token is invalid (401/403), not retrying")
          return problem
        } else if (problem.kind === "canceled") {
          // If the request was programatically canceled, don't retry
          console.error("[AUTH_API] refresh: Request canceled, not retrying")
          return problem
        }

        lastProblem = problem

        // Conditionally retry if we have attempts left
        if (attempt < MAX_ATTEMPTS) {
          const delayMs = BASE_DELAY_MS * Math.pow(attempt, 2) // Quadratic backoff
          console.debug(`[AUTH_API] refresh: Waiting ${delayMs}ms before retry...`)
          await delay(delayMs)
        }
      }
    }

    // If we've exhausted all retries, return the last problem or a fallback problem
    console.error("[AUTH_API] refresh: All retry attempts failed")
    return lastProblem || { kind: "unknown", temporary: true, data: null }
  }
}
